import { useRef } from "react";

import InformationCircle from "@impero-com/common/img/information-circle.svg";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { Svg, Tooltip, TST_COLUMN_SIZE, TSTTable } from "@impero-com/ui";
import {
    createColumnHelper,
    getCoreRowModel,
    useReactTable,
} from "@tanstack/react-table";

import { EntityView } from "api/generated/model";

import * as styles from "./DescendantsEntitiesTable.css";

export interface DescendantsTableEntity {
    entity: EntityView;
    paths: string[];
}

interface DescendantsEntitiesTableProps {
    entities: Array<DescendantsTableEntity>;
}

export function DescendantsEntitiesTable({
    entities,
}: DescendantsEntitiesTableProps) {
    const { t } = useTranslation();
    const columns = buildColumns(t);
    const tableRef = useRef<HTMLDivElement>(null);

    const table = useReactTable({
        data: entities,
        columns,
        enableSorting: false,
        getCoreRowModel: getCoreRowModel<DescendantsTableEntity>(),
    });

    return (
        <TSTTable
            tableRef={tableRef}
            table={table}
            noResultsFallback={t("NoResults")}
            noSourceRowsFallback={t("NoResults")}
        />
    );
}

interface InheritancePathTooltipProps {
    paths: string[];
    t: TFunction;
}

function InheritancePathTooltip({ paths, t }: InheritancePathTooltipProps) {
    const linkedTo = t("RiskDirectory_NodeUpdate_Entities_LinkedTo");
    const title =
        paths.length === 1
            ? `${linkedTo} ${paths[0]}`
            : `${linkedTo}\n${paths.join("\n")}`;
    return (
        <div className={styles.infoContainer}>
            <Tooltip title={title}>
                <span>
                    <Svg src={InformationCircle} className={styles.infoIcon} />
                </span>
            </Tooltip>
        </div>
    );
}

const columnHelper = createColumnHelper<DescendantsTableEntity>();

function buildColumns(t: TFunction) {
    return [
        columnHelper.accessor((row) => row.entity.name, {
            id: "name",
            cell: (info) => (
                <span className={styles.descendantEntity}>
                    {info.getValue()}
                </span>
            ),
            size: TST_COLUMN_SIZE.XL,
        }),
        columnHelper.display({
            id: "actions",
            size: TST_COLUMN_SIZE.CHECKBOX,
            meta: { fixedWidth: true },
            cell: (info) => (
                <InheritancePathTooltip paths={info.row.original.paths} t={t} />
            ),
        }),
    ];
}
